"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import staticData from "@/public/data/landing.json";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { trackAnalyticsEvent } from "@/shared/analytics/trackAnalyticsEvent";
import { useEffect, useState } from "react";

interface CallSupportProps {
  phone: string;
}

const AUTO_OPEN_STORAGE_KEY = "mdsafar.call-support.auto-opened";
const EXPANDED_AUTO_CLOSE_DELAY_MS = 3000;

export default function CallSupport({ phone }: CallSupportProps) {
  const { label } = staticData.callSupport;
  const [isExpanded, setIsExpanded] = useState(false);
  const normalizedPhone = normalizePhoneForHref(phone);

  useEffect(() => {
    if (window.sessionStorage.getItem(AUTO_OPEN_STORAGE_KEY) === "1") return;

    window.sessionStorage.setItem(AUTO_OPEN_STORAGE_KEY, "1");
    const openTimeout = window.setTimeout(() => {
      setIsExpanded(true);
      trackCallSupportEvent("call_support_auto_opened", phone);
    }, 0);

    return () => {
      window.clearTimeout(openTimeout);
    };
  }, [phone]);

  useEffect(() => {
    if (!isExpanded) return;

    const closeTimeout = window.setTimeout(() => {
      setIsExpanded(false);
    }, EXPANDED_AUTO_CLOSE_DELAY_MS);

    return () => {
      window.clearTimeout(closeTimeout);
    };
  }, [isExpanded]);

  const trackClick = () => {
    sendGTMEvent({
      event: "call_support",
      label,
      value: phone,
    });
    trackCallSupportEvent("call_support_clicked", phone);
  };

  const handleCollapsedClick = () => {
    setIsExpanded(true);
    trackCallSupportEvent("call_support_revealed", phone);
  };

  return (
    <div
      className="mdsafar-call-support fixed left-0 top-[58vh] z-50 md:hidden"
      dir="rtl"
    >
      {isExpanded ? (
        <a
          href={`tel:${normalizedPhone}`}
          onClick={trackClick}
          className="flex h-12 translate-x-0 items-center gap-2 rounded-r-2xl bg-primary px-4 pl-5 text-white shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition-all duration-300"
          data-analytics-event="cta_click"
          data-analytics-id="floating-call-support"
          data-analytics-name="رزرو تلفنی"
          data-analytics-type="floating_call_button"
          data-analytics-funnel="support"
          data-analytics-step="call_support_click"
          data-analytics-property-phone={phone}
          aria-label={`${label} ${phone}`}
        >
          <PhosphorIcon
            name="Phone"
            size={23}
            weight="fill"
            className="scale-x-[-1] text-white"
          />
          <span className="whitespace-nowrap text-sm font-extrabold">
            {label}
          </span>
          <span className="whitespace-nowrap text-xs font-bold opacity-95">
            {phone}
          </span>
        </a>
      ) : (
        <button
          type="button"
          onClick={handleCollapsedClick}
          className="grid h-12 w-14 -translate-x-1 place-items-center rounded-r-2xl bg-primary text-white shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition-all duration-300 hover:translate-x-0"
          data-analytics-event="cta_click"
          data-analytics-id="floating-call-support"
          data-analytics-name="رزرو تلفنی"
          data-analytics-type="floating_call_button"
          data-analytics-funnel="support"
          data-analytics-step="call_support_reveal"
          aria-label={label}
        >
          <PhosphorIcon
            name="Phone"
            size={24}
            weight="fill"
            className="scale-x-[-1] text-white"
          />
        </button>
      )}
    </div>
  );
}

function trackCallSupportEvent(eventName: string, phone: string) {
  trackAnalyticsEvent({
    eventName,
    element: {
      id: "floating-call-support",
      name: "رزرو تلفنی",
      type: "floating_call_button",
    },
    funnel: "support",
    step: eventName,
    properties: {
      phone,
    },
  });
}

function normalizePhoneForHref(phone: string) {
  return phone.replace(/[^\d+]/g, "");
}
